import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { Box, Typography, Switch, FormControlLabel } from "@mui/material";
import { createNotification, CustomTextField } from "../../../../../../../common/components";
import { ApiService, PackageManagementService } from "../../../../../../../api";
import { translate } from "../../../../../../../common/providers";
import IconPicker from "./IconPicker";
import LanguageFields from "./LanguageFields";
import { SUPPORTED_LANGUAGES } from "./constants";
import styles from "./PackageInfoForm.module.css";

const PackageInfoForm = forwardRef(
	({ onClose, onRefreshList = null, onUpdateRow = null, onValidityChange = null, pck = null }, ref) => {
		const cancelTokenSourceRef = useRef();

		useEffect(() => {
			cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
			return () => {
				ApiService.cancelTokens(cancelTokenSourceRef.current);
			};
		}, []);

		const initializeMetadata = () => {
			const defaultMetadata = SUPPORTED_LANGUAGES.reduce((acc, lang) => {
				acc[lang.code] = {
					officialName: "",
					shortDescription: "",
					hoverDescription: "",
				};
				return acc;
			}, {});

			if (pck) {
				SUPPORTED_LANGUAGES.forEach((lang) => {
					const langData = pck.description?.[lang.code] || {};
					defaultMetadata[lang.code] = {
						officialName: langData.officialName || "",
						shortDescription: langData.shortDescription || "",
						hoverDescription: langData.hoverDescription || "",
					};
				});
			}

			return defaultMetadata;
		};

		const [internalName, setInternalName] = useState(pck?.internalName || "");
		const [selectedIcon, setSelectedIcon] = useState(pck?.iconSvg || "");
		const [forceDisplay, setForceDisplay] = useState(pck?.forceDisplay || false);
		const [languageMetadata, setLanguageMetadata] = useState(initializeMetadata());

		const handleInternalNameChange = (e) => {
			setInternalName(e?.target?.value);
		};

		const handleIconChange = (e) => {
			setSelectedIcon(e?.target?.value);
		};

		const handleForceDisplayChange = (e) => {
			setForceDisplay(e.target.checked);
		};

		const handleMetadataChange = (langCode, field, value) => {
			setLanguageMetadata((prev) => ({
				...prev,
				[langCode]: {
					...prev[langCode],
					[field]: value,
				},
			}));
		};

		const isFormValid = () => {
			if (internalName.length < 2) {
				return false;
			}

			if (!selectedIcon) {
				return false;
			}

			const hasCompleteLanguage = SUPPORTED_LANGUAGES.some((lang) => {
				const meta = languageMetadata[lang.code];
				return meta.officialName && meta.shortDescription;
			});

			return hasCompleteLanguage;
		};

		useEffect(() => {
			if (onValidityChange) {
				onValidityChange(isFormValid());
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [internalName, selectedIcon, languageMetadata]);

		const handleSubmit = () => {
			const description = {};

			SUPPORTED_LANGUAGES.forEach((lang) => {
				const meta = languageMetadata[lang.code];
				if (meta.officialName || meta.shortDescription || meta.hoverDescription) {
					description[lang.code] = {};
					if (meta.officialName) {
						description[lang.code].officialName = meta.officialName;
					}
					if (meta.shortDescription) {
						description[lang.code].shortDescription = meta.shortDescription;
					}
					if (meta.hoverDescription) {
						description[lang.code].hoverDescription = meta.hoverDescription;
					}
				}
			});

			const payload = {
				internalName,
				iconSvg: selectedIcon,
				forceDisplay,
				description,
			};

			const promise = pck
				? PackageManagementService.updatePackage(
						{ packageId: pck?.id },
						payload,
						cancelTokenSourceRef.current.token
				  )
				: PackageManagementService.createPackage(payload, cancelTokenSourceRef.current.token);

			promise
				.then((data) => {
					if (onUpdateRow && typeof onUpdateRow === "function") {
						onUpdateRow(pck?.id, {
							internalName,
							iconSvg: selectedIcon,
							forceDisplay,
							description,
						});
					}
					createNotification({
						type: "success",
						message: pck
							? translate("dsi.package-management.panel.info.update.success")
							: translate("dsi.package-management.panel.creation.success"),
					});
					if (!pck && onRefreshList) {
						onRefreshList({ ...data, ...payload, companies: 0, features: 0, projects: 0 });
					}
					onClose();
				})
				.catch((err) => {
					console.error(err);
					createNotification({
						type: "error",
						message: translate("dsi.package-management.panel.error"),
					});
				});
		};

		useImperativeHandle(ref, () => ({
			submit: handleSubmit,
			isFormValid,
		}));

		return (
			<Box className={styles.container}>
				<Box className={styles.firstRow}>
					<Box className={styles.fieldSection}>
						<Typography className={styles.fieldLabel}>
							{translate("dsi.package-management.panel.info.internal-name")}
							<span className={styles.required}>*</span>
						</Typography>
						<CustomTextField
							fullWidth
							placeholder={translate("dsi.package-management.panel.info.placeholder.internal-name")}
							size="small"
							value={internalName}
							variant="outlined"
							onChange={handleInternalNameChange}
						/>
					</Box>

					<Box className={styles.forceDisplaySection}>
						<FormControlLabel
							control={
								<Switch checked={forceDisplay} color="primary" onChange={handleForceDisplayChange} />
							}
							label={translate("dsi.package-management.panel.info.force-display")}
						/>
					</Box>
				</Box>

				<Box className={styles.iconRow}>
					<Box className={styles.fieldSection}>
						<Typography className={styles.fieldLabel}>
							{translate("dsi.package-management.panel.info.icon-choice")}
							<span className={styles.required}>*</span>
						</Typography>
						<IconPicker
							label="Select an icon"
							size="small"
							value={selectedIcon}
							onChange={handleIconChange}
						/>
					</Box>
				</Box>

				<Box className={styles.fieldSection}>
					<Typography className={styles.fieldLabel}>
						{translate("dsi.package-management.panel.info.official-name")}
						<span className={styles.required}>*</span>
					</Typography>
					<LanguageFields
						fieldName="officialName"
						fieldType="text"
						maxLength={30}
						metadata={languageMetadata}
						onChange={handleMetadataChange}
					/>
				</Box>

				<Box className={styles.fieldSection}>
					<Typography className={styles.fieldLabel}>
						{translate("dsi.package-management.panel.info.short-description")}
						<span className={styles.required}>*</span>
					</Typography>
					<LanguageFields
						fieldName="shortDescription"
						fieldType="multiline"
						maxLength={150}
						metadata={languageMetadata}
						rows={3}
						onChange={handleMetadataChange}
					/>
				</Box>

				<Box className={styles.fieldSection}>
					<Typography className={styles.fieldLabel}>
						{translate("dsi.package-management.panel.info.hover-description")}
					</Typography>
					<LanguageFields
						fieldName="hoverDescription"
						fieldType="multiline"
						maxLength={300}
						metadata={languageMetadata}
						rows={4}
						onChange={handleMetadataChange}
					/>
				</Box>
			</Box>
		);
	}
);

export default PackageInfoForm;
